import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { AuthenticationService } from '../services/authentication.service';

const REFRESH_TOKEN_PATH = '/auth/refresh-token';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
    constructor(public authService: AuthenticationService) { }

    // Refresh em voo, compartilhado: vários 401 simultâneos renovam o token UMA vez e todos
    // reenviam com o token novo. Volta a null ao terminar (sucesso ou falha), então um 401
    // posterior começa um refresh novo em vez de esperar por um ciclo que já acabou.
    private refreshInFlight: Observable<string> | null = null;

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

        if (this.authService.getJwtToken()) {
            request = this.addToken(request, this.authService.getJwtToken());
        }

        return next.handle(request).pipe(catchError(error => {
            // O próprio refresh fica de fora: 401 nele é a sessão morta, não um pedido de
            // renovação. Sem essa guarda o refresh chamaria refresh e travaria o ciclo.
            const expiredToken = error instanceof HttpErrorResponse
                && error.status === 401
                && !request.url.includes(REFRESH_TOKEN_PATH);

            if (expiredToken) {
                return this.handle401Error(request, next);
            }

            // 403 NÃO derruba a sessão. Aqui havia um `logout()` para qualquer 403 de
            // qualquer endpoint, e ele deslogava o cliente já autenticado: 403 nesta API
            // quer dizer "esta ação não é permitida", nunca "seu token morreu" (sem token o
            // AuthMiddleware responde 400, expirado 401, inválido 500). O caso de produção
            // foi o cardápio: `GET /restaurant/{id}/discount-coupons/featured` roda em TODA
            // carga do menu e responde 403 MODULE_DISABLED em loja sem o módulo new_coupon,
            // então quem logava e navegava voltava deslogado. Quem prova que a sessão
            // acabou é o refresh, tratado em handle401Error.
            return throwError(error);
        }));
    }

    private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return this.sharedRefresh().pipe(
            take(1),
            // catchError ANTES do switchMap de propósito: só a falha do REFRESH desloga.
            // Depois do switchMap ele pegaria também o erro da request reenviada, e aí um
            // 500 qualquer no retry derrubaria a sessão.
            catchError(error => {
                this.authService.logout();
                return throwError(error);
            }),
            switchMap(token => next.handle(this.addToken(request, token))),
        );
    }

    private sharedRefresh(): Observable<string> {
        if (this.refreshInFlight) return this.refreshInFlight;

        this.refreshInFlight = this.authService.refreshToken().pipe(
            map((data: any) => data && data.token),
            // tap ANTES do shareReplay: roda uma vez, junto com a fonte, e não por subscriber.
            tap({
                next: () => { this.refreshInFlight = null; },
                error: () => { this.refreshInFlight = null; },
            }),
            shareReplay({ bufferSize: 1, refCount: false }),
        );

        return this.refreshInFlight;
    }

    private addToken(request: HttpRequest<any>, token: string) {
        if(request.url.includes('viacep')) return request;
        return request.clone({
            setHeaders: {
                'Authorization': `Bearer ${token}`
            }
        });
    }
}
